import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import AppLayout from '@/layouts/AppLayout';
import RequireAuth from '@/auth/RequireAuth';
import RequirePermission from '@/auth/RequirePermission';
import { accountingRoutes } from '@/modules/accounting/routes';
import { hrRoutes } from '@/modules/hr/routes';
import { fleetRoutes } from '@/modules/fleet/routes';
import { parkingPublicRoutes, parkingRoutes } from '@/modules/parking/routes';
import { propertyPublicRoutes, propertyRoutes } from '@/modules/property/routes';
import { signingPublicRoutes, signingRoutes } from '@/modules/signing/routes';
import { documentsOfficeRoutes, documentsRoutes } from '@/modules/documents/routes';
import { lendingRoutes } from '@/modules/lending/routes';
import { learningRoutes } from '@/modules/learning/routes';
import { hospitalRoutes } from '@/modules/hospital/routes';
import { budgetingRoutes } from '@/modules/budgeting/routes';
import { mePublicRoutes, meRoutes } from '@/modules/me/routes';
import { learnPublicRoutes, learnRoutes } from '@/modules/learn/routes';
import { drivePublicRoutes, driveRoutes } from '@/modules/drive/routes';
import { fieldRoutes } from '@/modules/field/routes';
import { portalRoutes } from '@/modules/portal/routes';
import { lendingChannelRoutes } from '@/modules/lending/channels/routes';
import { stockRoutes } from '@/modules/stock/routes';
import { Perm } from '@/auth/permissions';
import LoginPage from '@/pages/LoginPage';

// Pages and tabs load when first opened (NFR-04, docs §169).
const SignupPage = lazy(() => import('@/pages/SignupPage'));
const CareersPage = lazy(() => import('@/modules/hr/pages/CareersPages').then((m) => ({ default: m.CareersPage })));
const CareersVacancyPage = lazy(() => import('@/modules/hr/pages/CareersPages').then((m) => ({ default: m.CareersVacancyPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const SharedDocumentPage = lazy(() => import('@/pages/SharedDocumentPage'));
const RecycleBinPage = lazy(() => import('@/pages/RecycleBinPage'));
const OfflineDocumentsPage = lazy(() => import('@/pages/OfflineDocumentsPage'));
const DmsDashboardPage = lazy(() => import('@/pages/DmsDashboardPage'));
const ChangePasswordPage = lazy(() => import('@/pages/ChangePasswordPage'));
const AcceptPolicyPage = lazy(() => import('@/pages/AcceptPolicyPage'));
const AdministrationPage = lazy(() => import('@/pages/AdministrationPage'));
const DashboardsPage = lazy(() => import('@/pages/DashboardsPage'));
const AnalyticsPage = lazy(() => import('@/pages/AnalyticsPage'));
const HomePage = lazy(() => import('@/pages/HomePage'));
const StrategyPage = lazy(() => import('@/pages/StrategyPage'));
const StrategyFrameworkPage = lazy(() => import('@/pages/StrategyFrameworkPage'));
const KpisPage = lazy(() => import('@/pages/KpisPage'));
const KpiDetailPage = lazy(() => import('@/pages/KpiDetailPage'));
const WorkflowsPage = lazy(() => import('@/pages/WorkflowsPage'));
const ApprovalsPage = lazy(() => import('@/pages/ApprovalsPage'));
const InitiativesPage = lazy(() => import('@/pages/InitiativesPage'));
const ProjectDetailPage = lazy(() => import('@/pages/ProjectDetailPage'));
const EvidenceRepositoryPage = lazy(() => import('@/pages/EvidenceRepositoryPage'));
const MeetingsPage = lazy(() => import('@/pages/MeetingsPage'));
const CalendarPage = lazy(() => import('@/pages/CalendarPage'));
const MeetingSummaryPage = lazy(() => import('@/pages/MeetingSummaryPage'));
const MeetingDetailPage = lazy(() => import('@/pages/MeetingDetailPage'));
const LiveMeetingPage = lazy(() => import('@/pages/LiveMeetingPage'));
const BoardPacksPage = lazy(() => import('@/pages/BoardPacksPage'));
const BoardPackBuilderPage = lazy(() => import('@/pages/BoardPackBuilderPage'));
const MyBoardPacksPage = lazy(() => import('@/pages/MyBoardPacksPage'));
const DecisionsPage = lazy(() => import('@/pages/DecisionsPage'));
const ActionsPage = lazy(() => import('@/pages/ActionsPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'));
const MentionsPage = lazy(() => import('@/pages/MentionsPage'));
const AuditPage = lazy(() => import('@/pages/AuditPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const WebhooksPage = lazy(() => import('@/pages/WebhooksPage'));
const IntegrationsPage = lazy(() => import('@/pages/IntegrationsPage'));
const ApiKeysPage = lazy(() => import('@/pages/ApiKeysPage'));
const HealthPage = lazy(() => import('@/pages/HealthPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const QuestionnairesPage = lazy(() => import('@/pages/QuestionnairesPage'));
const RisksPage = lazy(() => import('@/pages/RisksPage'));
const RiskDetailPage = lazy(() => import('@/pages/RiskDetailPage'));

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/signup', element: <SignupPage /> },
  // The public careers page: anonymous, outside the signed-in layout. The server shows nothing for an organisation that has not opted in.
  { path: '/careers/:orgSlug', element: <CareersPage /> },
  { path: '/careers/:orgSlug/:vacancySlug', element: <CareersVacancyPage /> },
  // Annova Me's anonymous pages: the verify page behind a document's QR code, and a new starter's pre-start link.
  ...mePublicRoutes,
  ...learnPublicRoutes,
  // The driver app's own sign-in (organisation, driver number, PIN).
  ...drivePublicRoutes,
  ...parkingPublicRoutes,
  ...propertyPublicRoutes,
  // Outside signers' invitation links (docs §219): anonymous, outside the signed-in layout.
  ...signingPublicRoutes,
  // Outside readers' share links (docs §249): anonymous, outside the signed-in layout.
  { path: '/share/:orgSlug/:token', element: <SharedDocumentPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  {
    path: '/',
    element: <RequireAuth />,
    children: [
      // Annova Me: signed in, but in its own mobile-first frame rather than the main layout.
      ...meRoutes,
      // The learner frame (/learn, docs §266): its own mobile-first frame as well.
      ...learnRoutes,
      // The driver app (/drive): its own mobile frame too.
      ...driveRoutes,
      // The property field app (/field) and the occupier, contractor and owner portals (docs §215): their own frames.
      ...fieldRoutes,
      ...portalRoutes,
      // The borrower and collector portals (docs §237): their own frames too.
      ...lendingChannelRoutes,
      // The Outlook add-in's task pane (docs §259): its own frame inside Outlook.
      ...documentsOfficeRoutes,
      {
        element: <AppLayout />,
        children: [
          // Home needs no permission: everything on it is the person's own by membership, register or distribution.
          { index: true, element: <HomePage /> },
          { path: 'home', element: <HomePage /> },
          {
            path: 'dashboards',
            element: (
              <RequirePermission permission={Perm.Dashboard.View}>
                <DashboardsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'analytics',
            element: (
              <RequirePermission permission={Perm.Analytics.View}>
                <AnalyticsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'strategy',
            element: (
              <RequirePermission permission={Perm.Strategy.View}>
                <StrategyPage />
              </RequirePermission>
            ),
          },
          {
            path: 'strategy-framework',
            element: (
              <RequirePermission permission={Perm.Strategy.View}>
                <StrategyFrameworkPage />
              </RequirePermission>
            ),
          },
          {
            path: 'kpis',
            element: (
              <RequirePermission permission={Perm.Kpis.View}>
                <KpisPage />
              </RequirePermission>
            ),
          },
          {
            path: 'kpis/:id',
            element: (
              <RequirePermission permission={Perm.Kpis.View}>
                <KpiDetailPage />
              </RequirePermission>
            ),
          },
          // The performance grid now lives on the KPIs page; old links and bookmarks still land there.
          { path: 'performance', element: <Navigate to="/kpis" replace /> },
          {
            path: 'initiatives',
            element: (
              <RequirePermission permission={Perm.Initiatives.View}>
                <InitiativesPage />
              </RequirePermission>
            ),
          },
          {
            path: 'projects/:id',
            element: (
              <RequirePermission permission={Perm.Initiatives.View}>
                <ProjectDetailPage />
              </RequirePermission>
            ),
          },
          {
            path: 'evidence',
            element: (
              <RequirePermission permission={Perm.Evidence.View}>
                <EvidenceRepositoryPage />
              </RequirePermission>
            ),
          },
          {
            // The recycle bin (docs §257): everyone sees what they deleted; the records managers see it all.
            path: 'evidence/recycle-bin',
            element: (
              <RequirePermission permission={Perm.Evidence.View}>
                <RecycleBinPage />
              </RequirePermission>
            ),
          },
          {
            // The document administrators' dashboard (docs §260).
            path: 'evidence/dashboard',
            element: (
              <RequirePermission anyOf={[Perm.Evidence.Manage, Perm.Documents.Admin, Perm.Documents.Records]}>
                <DmsDashboardPage />
              </RequirePermission>
            ),
          },
          {
            // Documents kept on this device to read offline (docs §259).
            path: 'evidence/offline',
            element: (
              <RequirePermission permission={Perm.Evidence.View}>
                <OfflineDocumentsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'calendar',
            element: (
              <RequirePermission permission={Perm.Governance.View}>
                <CalendarPage />
              </RequirePermission>
            ),
          },
          {
            path: 'reviews',
            element: (
              <RequirePermission permission={Perm.Governance.View}>
                <MeetingsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'reviews/:id',
            element: (
              <RequirePermission permission={Perm.Governance.View}>
                <MeetingDetailPage />
              </RequirePermission>
            ),
          },
          {
            path: 'reviews/:id/summary',
            element: <MeetingSummaryPage />,
          },
          {
            path: 'reviews/:id/live',
            element: (
              <RequirePermission permission={Perm.Governance.View}>
                <LiveMeetingPage />
              </RequirePermission>
            ),
          },
          {
            path: 'questionnaires',
            element: (
              <RequirePermission permission={Perm.Governance.View}>
                <QuestionnairesPage />
              </RequirePermission>
            ),
          },
          {
            path: 'questionnaires/:id',
            element: (
              <RequirePermission permission={Perm.Governance.View}>
                <QuestionnairesPage />
              </RequirePermission>
            ),
          },
          {
            path: 'board-packs',
            element: (
              <RequirePermission permission={Perm.BoardPacks.View}>
                <BoardPacksPage />
              </RequirePermission>
            ),
          },
          {
            // No permission gate: someone invited to work on one pack opens it from the invitation, and
            // the API decides per pack (BoardPacks.View or an invitation); a stranger gets its 403 page.
            path: 'board-packs/:id',
            element: <BoardPackBuilderPage />,
          },
          {
            // No permission gate: the distribution record is the authorisation, so a board member
            // can read what was sent to them without rights over the wider pack library.
            path: 'my-board-packs',
            element: <MyBoardPacksPage />,
          },
          {
            path: 'decisions',
            element: (
              <RequirePermission permission={Perm.Decisions.View}>
                <DecisionsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'actions',
            element: (
              <RequirePermission permission={Perm.Actions.View}>
                <ActionsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'risks',
            element: (
              <RequirePermission permission={Perm.Risks.View}>
                <RisksPage />
              </RequirePermission>
            ),
          },
          {
            path: 'risks/:id',
            element: (
              <RequirePermission permission={Perm.Risks.View}>
                <RiskDetailPage />
              </RequirePermission>
            ),
          },
          {
            path: 'approvals',
            element: (
              <RequirePermission anyOf={[Perm.Kpis.Review, Perm.Accounting.Post, Perm.Hr.Approve, Perm.Hr.ApprovePayroll, Perm.Fleet.Approve, Perm.Parking.Approve, Perm.Property.ApproveLeasing]}>
                <ApprovalsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'workflows',
            element: (
              <RequirePermission permission={Perm.Settings.Manage}>
                <WorkflowsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'reports',
            element: (
              <RequirePermission permission={Perm.Reports.View}>
                <ReportsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'audit',
            element: (
              <RequirePermission permission={Perm.Audit.View}>
                <AuditPage />
              </RequirePermission>
            ),
          },
          {
            path: 'webhooks',
            element: (
              <RequirePermission permission={Perm.Settings.Manage}>
                <WebhooksPage />
              </RequirePermission>
            ),
          },
          {
            path: 'privacy/:view?',
            element: (
              <RequirePermission anyOf={[Perm.Privacy.View, Perm.Privacy.Manage]}>
                <PrivacyPage />
              </RequirePermission>
            ),
          },
          {
            path: 'integrations',
            element: (
              <RequirePermission permission={Perm.Settings.Manage}>
                <IntegrationsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'api-keys',
            element: (
              <RequirePermission permission={Perm.Settings.Manage}>
                <ApiKeysPage />
              </RequirePermission>
            ),
          },
          {
            path: 'administration',
            element: (
              <RequirePermission anyOf={[Perm.Users.View, Perm.Roles.View, Perm.Settings.Manage, Perm.Platform.Manage, Perm.Risks.Configure]}>
                <AdministrationPage />
              </RequirePermission>
            ),
          },
          // Optional modules' routes: each page checks its module and shows the "not enabled" card otherwise.
          ...accountingRoutes,
          ...stockRoutes,
          ...hrRoutes,
          ...fleetRoutes,
          ...parkingRoutes,
          ...propertyRoutes,
          ...signingRoutes,
          ...documentsRoutes,
          ...lendingRoutes,
          ...learningRoutes,
          ...hospitalRoutes,
          ...budgetingRoutes,
          { path: 'notifications', element: <NotificationsPage /> },
          // No permission: a note that named you is yours to read, whatever else you may see.
          { path: 'mentions', element: <MentionsPage /> },
          { path: 'change-password', element: <ChangePasswordPage /> },
          { path: 'accept-policy', element: <AcceptPolicyPage /> },
          { path: 'health', element: <HealthPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
